import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import { FaFileExport } from "react-icons/fa";
import { months_list } from "../../Shared/List"
import Spinner from '../../Shared/Spinner'
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const LessPaidDetail = () => {

    const [unPaid, setUnPaid] = useState([])
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([])
    const [title, setTitle] = useState('');
    const [isLoading, setIsLoading] = useState(false)
    const gridRef = useRef();
    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {

        if (location.state){
            const {payload} = location.state

            fetchData({
                year: payload.year,
                month: payload.month,
                collector_id: payload.collector_id,
            })
            setTitle(`${payload.collector_name}, ${months_list[payload.month - 1].name} - ${payload.year} Less Paid Details`)

        }

    }, [])

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/un-paid/less-paid-details?year=${payload.year}&month=${payload.month}&collector_id=${payload.collector_id}`)
            setUnPaid(response?.data)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.debit += parseFloat(row.debit);
                data.credit += parseFloat(row.credit);
                data.discount += parseFloat(row.discount);
                data.bill_balance += parseFloat(row.bill_balance);
                return data;
            }, {debit: 0.00, credit: 0.00, discount: 0.00, bill_balance: 0.00});
            setPinnedBottomRowData([
                {
                    debit: parseFloat(output.debit).toFixed(2),
                    credit: parseFloat(output.credit).toFixed(2),
                    discount: parseFloat(output.discount).toFixed(2),
                    bill_balance: parseFloat(output.bill_balance).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load less paid details')
            }
        }
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: false,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "PID",
            field: "previous_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "ID",
            field: "location_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Name",
            field: "full_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "contact_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Credit",
            field: "credit",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Discount",
            field: "discount",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "BillBalance",
            field: "bill_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Rate",
            field: "rate_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Village",
            field: "village_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Zone",
            field: "zone_name",
            sortable: true,
            filter: true,
        },
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi
    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    const onFilterChanged = (params) => {
        // Get only the filtered data
        const filteredRows = [];
        params.api.forEachNodeAfterFilter(node => filteredRows.push(node.data));

        // Reduce filtered data
        const output = filteredRows.reduce((data, row) => {
            data.debit += Number(row.debit) || 0;
            data.credit += Number(row.credit) || 0;
            data.discount += Number(row.discount) || 0;
            data.bill_balance += Number(row.bill_balance) || 0;
            return data;
        }, { debit: 0.00, credit: 0.00, discount: 0.00, bill_balance: 0.00});

        // Update pinned bottom row with filtered values
        setPinnedBottomRowData([
            {
                debit: output.debit.toFixed(2),
                credit: output.credit.toFixed(2),
                discount: output.discount.toFixed(2),
                bill_balance: output.bill_balance.toFixed(2),
            }
        ]);

    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="row">
            <div className="col-md-12">
                <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
            </div>
        </div>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            onFilterChanged={onFilterChanged}

                            rowData={unPaid}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default LessPaidDetail